import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  ArrowLeft, Plus, Edit, Trash2, FileText, Mail, Home
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

const TemplateList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await fetch(`${API_URL}/api/email-templates/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.template_id !== deleteId));
        toast.success('Template deleted');
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="template-list-page">
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#f5f3ed]">Email Templates</h1>
              <p className="text-[#f5f3ed]/70 text-sm">Create and manage email templates</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/admin/email-templates/new')}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
            data-testid="new-template-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Template
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a55a]"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-[#2d5f3f]/30 rounded-xl border border-[#2d5f3f]">
            <Mail className="w-16 h-16 text-[#f5f3ed]/30 mx-auto mb-4" />
            <p className="text-[#f5f3ed]/50 mb-4">No email templates yet</p>
            <Button
              onClick={() => navigate('/admin/email-templates/new')}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Template
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {templates.map(template => (
              <div
                key={template.template_id}
                className="bg-[#2d5f3f]/30 rounded-xl p-5 border border-[#2d5f3f] hover:border-[#c9a55a]/50 transition-colors"
                data-testid={`template-${template.template_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#f5f3ed]">{template.name}</h3>
                      {template.has_booking_placeholders && (
                        <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">
                          Booking Template
                        </span>
                      )}
                    </div>
                    <p className="text-[#f5f3ed]/70 text-sm mb-2">
                      Subject: {template.subject}
                    </p>
                    {template.default_attachments.length > 0 && (
                      <div className="flex items-center gap-2 text-[#f5f3ed]/50 text-sm">
                        <FileText className="w-4 h-4" />
                        <span>{template.default_attachments.length} default attachment(s)</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/admin/email-templates/${template.template_id}`)}
                      className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
                    >
                      <Edit className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(template.template_id)}
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Template?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this email template. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplateList;
